import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Alert,
    Box,
    Chip,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DoneIcon from '@mui/icons-material/Done';
import { useLazyQuery } from '@apollo/client';
import { millingBatchesTemplate } from 'apollo/templates/milling';
import { MillingBatch, MillingBatchStatus, MillingFilters } from 'models/milling';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import MillingBatchDialog from './milling-batch-dialog.component';
import InfiniteScroll from 'react-infinite-scroll-component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';
import { Order } from 'models/order';

type MillingBatchesListProps = {
    filters: MillingFilters;
};
const MillingBatchesList: FC<MillingBatchesListProps> = ({ filters }: MillingBatchesListProps) => {
    const { t: translate } = useTranslation(['common', 'milling-jobs', 'orders']);
    const [lastOffset, setLastOffset] = useState(0);
    const [batches, setBatches] = useState<MillingBatch[]>([]);
    const [batchDialogOpen, setBatchDialogOpen] = useState(false);
    const [currentBatch, setCurrentBatch] = useState<MillingBatch>();
    const { showBoundary } = useDenthubErrorBoundary();

    const [millingBatches, { loading, fetchMore, refetch }] = useLazyQuery(millingBatchesTemplate, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onError: (error) => {
            showBoundary(error);
        },
        onCompleted: (data) => {
            setBatches(data.millingBatches);
            setLastOffset(data.millingBatches.length);
            if (currentBatch) {
                setCurrentBatch(
                    data.millingBatches.find(
                        (batch: MillingBatch) => batch._id?.toString() === currentBatch._id?.toString(),
                    ),
                );
            }
        },
    });

    useEffect(() => {
        millingBatches({
            variables: {
                limit: 20,
                offset: 0,
                filters: filters,
            },
        });
    }, [filters]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    const openBatchDialog = (batch: MillingBatch) => {
        setCurrentBatch(batch);
        setBatchDialogOpen(true);
    };

    const getOrderIds = (orders: Order[]) => {
        return orders?.map((order) => order.orderId)?.join(', ') || '';
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, batches?.length || 0);

    return (
        <Fragment>
            {loading && <LinearProgress color="secondary" />}
            {!loading && batches.length ? (
                <TableContainer component={Paper}>
                    <InfiniteScroll
                        dataLength={batches ? batches.length : 0}
                        next={handleFetchMore}
                        throttleTimeInMs={75}
                        hasMore={true}
                        loader={undefined}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 150 }}>{translate('common:STATUS')}1</TableCell>
                                    <TableCell>{translate('milling-jobs:BATCH_GROUP')}</TableCell>
                                    <TableCell>{translate('milling-jobs:BATCH')}</TableCell>
                                    <TableCell>{translate('orders:ORDERS')}</TableCell>
                                    <TableCell>{translate('milling-jobs:PRODUCED_BY')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {batches.map((batch) => (
                                    <TableRow
                                        key={batch._id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => openBatchDialog(batch)}
                                    >
                                        <TableCell>
                                            {batch.status === MillingBatchStatus.IN_PRODUCTION ? (
                                                <Chip
                                                    variant="outlined"
                                                    label={translate('milling-jobs:IN_PRODUCTION')}
                                                    color="info"
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<DoneIcon />}
                                                    label={translate('milling-jobs:FINISHED')}
                                                    color="success"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {batch.jobType}: {batch.material} {batch.color}
                                        </TableCell>
                                        <TableCell>{batch.batchNumber}</TableCell>
                                        <TableCell>{getOrderIds(batch.orders)}</TableCell>
                                        <TableCell>
                                            <Box>{formatDate(batch.createdAt, 'datetime')}</Box>
                                            <Typography variant="body2" color="grey.700">
                                                {batch.createdBy.fullName}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </InfiniteScroll>
                </TableContainer>
            ) : (
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('milling-jobs:NO_MILLING_BATCHES')}
                </Alert>
            )}
            <MillingBatchDialog
                batch={currentBatch}
                open={batchDialogOpen}
                onClose={() => setBatchDialogOpen(false)}
                onBatchChanged={() => {
                    if (refetch) {
                        refetch();
                    }
                }}
            />
        </Fragment>
    );
};

export default MillingBatchesList;
